import { useMemo } from 'react';
import { ChartData } from '../types';

// Helper to convert month name to number
export const getMonthNumber = (monthName: string) => {
  const months = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  return months[monthName as keyof typeof months] || 0;
};

// Parse dates from chart data
export const useParsedDates = (chartData: ChartData[]) => {
  return useMemo(() => {
    return chartData.map((d) => {
      // Parse FormattedDate which is in ISO format (YYYY-MM-DD)
      if (d.FormattedDate) return new Date(d.FormattedDate);

      // Fallback to ShortDate if FormattedDate is not available
      const dateParts = d.ShortDate.split(' ');
      const month = dateParts[0];
      const day = parseInt(dateParts[1]);
      const currentYear = new Date().getFullYear();
      return new Date(currentYear, getMonthNumber(month), day);
    });
  }, [chartData]);
};

// Utility to format a date as YYYY-MM-DD
export function formatDateISO(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
}

// Format a FormattedDate key (YYYY-MM-DD or YYYY-MM-DDTHH:MM) for display
export function formatChartDate(
  formattedDate: string,
  locale: string,
  format: string,
): string {
  const hasTime = formattedDate.includes('T');
  const date = new Date(formattedDate);

  const timeSuffix = hasTime
    ? ` ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    : '';

  if (format === 'month' && !hasTime) {
    try {
      return date.toLocaleDateString(locale, {
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return date.toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric',
      });
    }
  }

  let dateStr: string;
  try {
    dateStr = date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    dateStr = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }

  return dateStr + timeSuffix;
}
